import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import { Hyphenated } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import getImageUrl from '@configuratorware/configurator-frontendgui/App/Utils/getImageUrl';

const useStyles = makeStyles(
  (theme) => {
    const captionMaxHeight = `calc(${theme.creator.productPart.captionPaddingTop} + ${theme.creator.productPart.lineHeight} * 2)`;
    const iconMaxHeigth = `calc(${theme.creator.productPart.itemSize} - ${captionMaxHeight}  - ${theme.creator.productPart.captionPaddingTop})`;

    const iconSizeWithoutMarker = `calc(${theme.creator.productPart.itemSize} - ${theme.creator.productPart.spaceBetweenItemAndMarker})`;

    const contentMargin = {
      right: '0 0 0 ' + theme.creator.productPart.spaceBetweenItemAndMarker,
      left: '0 ' + theme.creator.productPart.spaceBetweenItemAndMarker + ' 0 0',
    };

    return {
      root: {
        [theme.breakpoints.up('sm')]: {
          width: theme.creator.productPart.selectedItemSize,
        },
        width: theme.creator.productPart.itemSize,
        height: 'auto',
        minHeight: '89px',

        '&.horizontal': {
          padding: '0 10px',
        },
        '&.vertical': {
          marginLeft: 'auto',
          display: 'flex',
          flexDirection: 'row-reverse',
        },
      },
      content: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        textAlign: 'center',
        position: 'relative',

        '&.horizontal': {
          width: theme.creator.productPart.itemSize,
          height: 'auto',
          float: MARKER_POSITION.top,
          whiteSpace: 'normal',
          '& span': {
            marginBottom: 0,
          },
        },
        '&.vertical': {
          width: iconSizeWithoutMarker,
          maxHeight: 120,
          float: theme.creator.productPartsList.markerVerticalPosition,
          margin:
            theme.creator.productPartsList.markerVerticalPosition === MARKER_POSITION.right
              ? contentMargin.left
              : contentMargin.right,
        },
      },
      icon: {
        maxHeight: iconMaxHeigth,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
        display: 'block',
        '&.horizontal': {
          width: theme.creator.productPart.itemSize,
          height: 'calc(0.75*' + theme.creator.productPart.itemSize + ')',
        },
        '&.vertical': {
          width: iconSizeWithoutMarker,
          height: 'calc(0.75*' + iconSizeWithoutMarker + ')',
        },
      },
      marker: {
        backgroundColor: 'transparent',
        '&.horizontal': {
          width: theme.creator.productPart.itemSize,
          height: theme.creator.productPart.markerThickness,
        },
        '&.vertical': {
          width: theme.creator.productPart.markerThickness,
          height: `calc(${theme.creator.productPart.itemSize} - 24px)`,
          float: theme.creator.productPartsList.markerVerticalPosition,
        },
        '&.selected': {
          [theme.breakpoints.down('sm')]: {
            marginBottom: 4,
          },
          backgroundColor: theme.palette.primary.main,
        },
      },
      rootTypography: {
        padding: '3px 0 0 7px',
        marginBottom: 5,
        display: '-webkit-box',
        overflow: 'hidden',
        wordWrap: 'break-word',
        maxWidth: theme.creator.productPart.itemSize,
        lineHeight: 1,
        boxOrient: 'vertical',
        lineClamp: 3,
        minHeight: 20,
      },
      orderNumber: {
        backgroundColor: 'lightgrey',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        marginBottom: '10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 'bold',
      },
    };
  },
  { name: 'ProductPart' }
);

const ProductPart = ({ onSelect, optionclassification, selectedOption, verticalMode, setAnchorRef }) => {
  const selected =
    optionclassification.type === 'group'
      ? get(optionclassification, 'identifier', '').includes(get(selectedOption, 'identifier', null))
      : get(selectedOption, 'identifier', null) === get(optionclassification, 'identifier', '');
  const direction = verticalMode ? 'vertical' : 'horizontal';
  const classes = useStyles();

  const displayImage = () => {
    const imageUrl =
      optionclassification.thumbnail && optionclassification.thumbnail !== ''
        ? getImageUrl(optionclassification.thumbnail)
        : null;
    return (
      <i
        className={`${classes.icon} ${direction}`}
        style={imageUrl ? { backgroundImage: 'url(' + imageUrl + ')' } : {}}
      />
    );
  };

  const displayText = () => {
    return (
      <Typography className={classes.rootTypography} variant="body1" component="span">
        <Hyphenated>{optionclassification.title}</Hyphenated>
      </Typography>
    );
  };

  const onClick = (event) => {
    event.stopPropagation();
    onSelect(optionclassification);
  };

  return (
    <div className={clsx(classes.root, direction, customClassName('component'))} ref={setAnchorRef}>
      <div className={`${classes.marker} ${direction} ${selected ? 'selected' : ''}`} />
      <div className={`${classes.content} ${direction}`} onClick={onClick}>
        {displayImage()}
        {displayText()}
      </div>
    </div>
  );
};

const MARKER_POSITION = {
  left: 'left',
  right: 'right',
  top: 'top',
};

ProductPart.defaultProps = {
  optionclassification: {},
  onSelect: () => {},
  verticalMode: true,
};

ProductPart.propTypes = {
  onSelect: PropTypes.func,
  optionclassification: PropTypes.object,
  selectedOption: PropTypes.object,
  verticalMode: PropTypes.bool,
  setAnchorRef: PropTypes.func,
};

export default ProductPart;
